import type { Me, ProfileUpdate, RaceCategory } from "../api/types";

export const CATEGORY_OPTIONS: readonly RaceCategory[] = ["A", "B", "C", "D"];

export interface ProfileFormValues {
  displayName: string;
  weightKg: string;
  heightCm: string;
  ftpWatts: string;
  category: RaceCategory;
  healthDataConsent: boolean;
}

export type ProfileFormErrors = Partial<Record<keyof ProfileFormValues, string>>;

export function profileFormValues(me: Me | null): ProfileFormValues {
  return {
    displayName: me?.profile?.displayName ?? "",
    weightKg:
      me?.profile?.weightKg === null || me?.profile?.weightKg === undefined
        ? ""
        : String(me.profile.weightKg),
    heightCm:
      me?.profile?.heightCm === null || me?.profile?.heightCm === undefined
        ? ""
        : String(me.profile.heightCm),
    ftpWatts:
      me?.profile?.ftpWatts === null || me?.profile?.ftpWatts === undefined
        ? ""
        : String(me.profile.ftpWatts),
    category: me?.profile?.category ?? "D",
    healthDataConsent: Boolean(me?.profile?.healthDataConsentAt),
  };
}

function parseOptionalNumber(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : Number.NaN;
}

export function validateProfileForm(values: ProfileFormValues): ProfileFormErrors {
  const errors: ProfileFormErrors = {};
  const displayName = values.displayName.trim();
  if (displayName.length < 2) {
    errors.displayName = "Use at least two characters.";
  } else if (displayName.length > 40) {
    errors.displayName = "Keep the display name under 40 characters.";
  }

  const weight = parseOptionalNumber(values.weightKg);
  if (weight === null) {
    errors.weightKg = "Weight is required for race physics.";
  } else if (Number.isNaN(weight) || weight < 30 || weight > 250) {
    errors.weightKg = "Weight must be between 30 and 250 kg.";
  }

  const height = parseOptionalNumber(values.heightCm);
  if (height === null) {
    errors.heightCm = "Height is required before your first race.";
  } else if (Number.isNaN(height) || height < 100 || height > 250) {
    errors.heightCm = "Height must be between 100 and 250 cm.";
  }

  const ftp = parseOptionalNumber(values.ftpWatts);
  if (ftp !== null && (Number.isNaN(ftp) || !Number.isInteger(ftp) || ftp < 0 || ftp > 2500)) {
    errors.ftpWatts = "FTP must be a whole number between 0 and 2500 W.";
  }

  if (!CATEGORY_OPTIONS.includes(values.category)) {
    errors.category = "Choose a category.";
  }

  return errors;
}

export function profileUpdateFromForm(values: ProfileFormValues): ProfileUpdate {
  const weight = parseOptionalNumber(values.weightKg);
  const height = parseOptionalNumber(values.heightCm);
  const ftp = parseOptionalNumber(values.ftpWatts);
  return {
    displayName: values.displayName.trim(),
    weightKg: weight === null ? null : weight,
    heightCm: height === null ? null : height,
    ftpWatts: ftp === null ? null : ftp,
    category: values.category,
    ...(values.healthDataConsent ? { healthDataConsent: true } : {}),
  };
}

/** The server requires a display name and body measurements before racing. */
export function isProfileReadyForRace(me: Me | null): boolean {
  const profile = me?.profile;
  if (!profile) return false;
  return Boolean(
    profile.displayName &&
      profile.weightKg !== null &&
      profile.weightKg > 0 &&
      profile.heightCm !== null &&
      profile.heightCm > 0,
  );
}
